import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { handleAuthHttpStatus, isAuthHttpStatus } from "../lib/authErrors";
import { cardBorderClassName } from "../lib/cardStyles";
import { loadTeamMembers, type TeamMember } from "../lib/teamMembers";
import { SectionHeading } from "./Card";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

export type EditableTransferRule = {
  id: string;
  description: string;
  keyword: string;
  number: string;
  email: string;
  mode: "screened" | "direct";
  message_to_text: boolean;
  message_to_email: boolean;
};

function newRow(): EditableTransferRule {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    description: "",
    keyword: "",
    number: "",
    email: "",
    mode: "screened",
    message_to_text: true,
    message_to_email: false,
  };
}

function apiRuleToRow(r: any, idx: number): EditableTransferRule {
  const screen = r?.screen !== false;
  return {
    id: `loaded-${idx}-${r?.keyword || idx}`,
    description: String(r?.description ?? ""),
    keyword: String(r?.keyword ?? r?.trigger ?? ""),
    number: String(r?.number ?? r?.phone ?? ""),
    email: String(r?.email ?? ""),
    mode: screen ? "screened" : "direct",
    message_to_text: r?.message_to_text !== false,
    message_to_email: Boolean(r?.message_to_email),
  };
}

function emailLooksValid(s: string): boolean {
  const t = s.trim();
  if (!t) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t);
}

function rowToPayload(row: EditableTransferRule) {
  return {
    description: row.description.trim(),
    keyword: row.keyword.trim(),
    number: row.number.trim(),
    email: row.email.trim(),
    screen: row.mode === "screened",
    message_to_text: row.message_to_text,
    message_to_email: row.message_to_email,
  };
}

function isBlankRow(row: EditableTransferRule): boolean {
  return ![row.description, row.keyword, row.number, row.email].some((v) => v.trim());
}

function isSendableRow(row: EditableTransferRule): boolean {
  return Boolean(row.keyword.trim() && row.number.trim());
}

function showEmailError(row: EditableTransferRule): boolean {
  return row.message_to_email && Boolean(row.email.trim()) && !emailLooksValid(row.email);
}

/** Blank rows are never sent, so they don't count as unsaved edits. */
function snapshotOf(rows: EditableTransferRule[]): string {
  return JSON.stringify(rows.filter((r) => !isBlankRow(r)).map(rowToPayload));
}

const INPUT_CLASS =
  "w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-sm text-white";

function RowActions({
  index,
  count,
  onMove,
  onRemove,
}: {
  index: number;
  count: number;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const base =
    "inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent";
  return (
    <div className="flex flex-wrap items-center gap-1">
      <button
        type="button"
        aria-label="Move up"
        onClick={() => onMove(-1)}
        disabled={index === 0}
        className={`${base} hover:text-white disabled:hover:text-gray-400`}
      >
        <i className="ri-arrow-up-s-line text-lg" aria-hidden />
      </button>
      <button
        type="button"
        aria-label="Move down"
        onClick={() => onMove(1)}
        disabled={index === count - 1}
        className={`${base} hover:text-white disabled:hover:text-gray-400`}
      >
        <i className="ri-arrow-down-s-line text-lg" aria-hidden />
      </button>
      <button
        type="button"
        aria-label="Remove rule"
        onClick={onRemove}
        disabled={count <= 1}
        className={`${base} hover:text-red-300 disabled:hover:text-gray-400`}
      >
        <i className="ri-delete-bin-line text-base" aria-hidden />
      </button>
    </div>
  );
}

function TeamMemberQuickSelect({
  isOpen,
  onToggle,
  onClose,
  onSelect,
}: {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSelect: (phone: string) => void;
}) {
  const [members, setMembers] = useState<TeamMember[]>(() => loadTeamMembers());
  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (isOpen) setMembers(loadTeamMembers());
  }, [isOpen]);

  // Fixed positioning so the menu isn't clipped by the table's overflow-x-auto wrapper.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setMenuPos({ top: rect.bottom + 4, left: rect.left });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [isOpen, onClose]);

  if (members.length === 0) return null;

  return (
    <div ref={containerRef} className="relative mt-1">
      <button
        ref={triggerRef}
        type="button"
        onClick={onToggle}
        className="text-xs text-purple-400 hover:text-purple-300 transition-colors cursor-pointer"
      >
        Quick-select team member
      </button>
      {isOpen && menuPos && (
        <div
          className="fixed z-50 min-w-[220px] rounded-lg border border-white/10 bg-[#1a1a24] py-1 shadow-lg"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {members.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-500">
              No team members — add them in the Team tab.
            </p>
          ) : (
            members.map((member) => (
              <button
                key={member.id}
                type="button"
                onClick={() => onSelect(member.phone)}
                className="w-full px-3 py-2 text-left text-xs text-gray-300 hover:bg-white/5 cursor-pointer"
              >
                <span className="text-white font-medium">{member.name}</span>
                {member.phone && (
                  <span className="block font-mono text-orange-400/80 mt-0.5">{member.phone}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function TransferRulesEditor({
  customerId,
}: {
  customerId: string;
}) {
  const [rows, setRows] = useState<EditableTransferRule[]>([]);
  const [status, setStatus] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invalidRowId, setInvalidRowId] = useState<string | null>(null);
  // Keyed by layout + row id: the table and the stacked cards both render a quick-select per row.
  const [quickSelectKey, setQuickSelectKey] = useState<string | null>(null);
  const closeQuickSelect = useCallback(() => setQuickSelectKey(null), []);
  const lastSyncedRef = useRef<string>(snapshotOf([]));
  const pendingFocusIdRef = useRef<string | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);

  const applyServerRows = useCallback((next: EditableTransferRule[]) => {
    lastSyncedRef.current = snapshotOf(next);
    setRows(next);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setStatus("");
    setInvalidRowId(null);
    try {
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/transfer-rules`,
        { method: "GET", credentials: "include" }
      );
      if (isAuthHttpStatus(res.status)) {
        void handleAuthHttpStatus(res.status, () => {
          setStatus("Unauthorized — log in again.");
        });
        applyServerRows([]);
        return;
      }
      if (!res.ok) {
        setStatus(`Load failed: HTTP ${res.status}`);
        applyServerRows([]);
        return;
      }
      const data = await res.json();
      const rules = Array.isArray(data?.rules) ? data.rules : [];
      applyServerRows(
        rules.length
          ? rules.map((r: any, i: number) => apiRuleToRow(r, i))
          : [newRow()]
      );
    } catch (e: any) {
      setStatus(e?.message || String(e));
      applyServerRows([newRow()]);
    } finally {
      setLoading(false);
    }
  }, [customerId, applyServerRows]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!status.startsWith("Saved")) return;
    const t = window.setTimeout(() => setStatus(""), 3000);
    return () => window.clearTimeout(t);
  }, [status]);

  useEffect(() => {
    const id = pendingFocusIdRef.current;
    if (!id) return;
    pendingFocusIdRef.current = null;
    const inputs = sectionRef.current?.querySelectorAll<HTMLInputElement>(
      `input[data-description-for="${CSS.escape(id)}"]`
    );
    Array.from(inputs ?? [])
      .find((el) => el.offsetParent !== null)
      ?.focus();
  }, [rows]);

  const reload = () => {
    if (
      snapshotOf(rows) !== lastSyncedRef.current &&
      !window.confirm("Discard unsaved changes and reload?")
    ) {
      return;
    }
    void load();
  };

  const save = async () => {
    setInvalidRowId(null);
    const incomplete = rows.findIndex((r) => !isBlankRow(r) && !isSendableRow(r));
    if (incomplete >= 0) {
      const row = rows[incomplete];
      const hasKeyword = Boolean(row.keyword.trim());
      const hasNumber = Boolean(row.number.trim());
      const need = hasKeyword
        ? "a phone number"
        : hasNumber
          ? "a keyword"
          : "a keyword and a phone number";
      setStatus(`Row ${incomplete + 1}: needs ${need}.`);
      setInvalidRowId(row.id);
      return;
    }
    setSaving(true);
    setStatus("");
    try {
      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        if (!isSendableRow(r)) continue;
        if (r.message_to_email && !emailLooksValid(r.email)) {
          setStatus(
            `Row ${i + 1}: when “Email message if unavailable” is checked, enter a valid message email address (e.g. team@example.com).`
          );
          setSaving(false);
          return;
        }
      }
      const payload = {
        rules: rows.filter(isSendableRow).map(rowToPayload),
      };
      const res = await fetch(
        `${API_BASE}/customers/${customerId}/transfer-rules`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const det = Array.isArray(data?.details)
          ? data.details.join("; ")
          : data?.error || "";
        setStatus(`Save failed: HTTP ${res.status} ${det}`);
        return;
      }
      setStatus("Saved transfer rules.");
      const saved = Array.isArray(data?.rules) ? data.rules : payload.rules;
      applyServerRows(
        saved.length
          ? saved.map((r: any, i: number) => apiRuleToRow(r, i))
          : [newRow()]
      );
    } catch (e: any) {
      setStatus(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const update = (id: string, patch: Partial<EditableTransferRule>) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...patch } : r))
    );
  };

  const remove = (id: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  const move = (id: string, dir: -1 | 1) => {
    setRows((prev) => {
      const i = prev.findIndex((r) => r.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const addRow = () => {
    const row = newRow();
    pendingFocusIdRef.current = row.id;
    setRows((prev) => [...prev, row]);
  };

  const renderQuickSelect = (r: EditableTransferRule, layout: "table" | "card") => {
    const key = `${layout}:${r.id}`;
    return (
      <TeamMemberQuickSelect
        isOpen={quickSelectKey === key}
        onToggle={() => setQuickSelectKey((prev) => (prev === key ? null : key))}
        onClose={closeQuickSelect}
        onSelect={(phone) => {
          update(r.id, { number: phone });
          setQuickSelectKey(null);
        }}
      />
    );
  };

  const renderEmailInput = (r: EditableTransferRule, extraClassName: string, ariaLabel?: string) => (
    <input
      type="email"
      autoComplete="off"
      aria-label={ariaLabel}
      className={`w-full rounded-lg border bg-black/40 px-3 py-2 text-sm text-white ${extraClassName} ${
        showEmailError(r) ? "border-amber-500/60" : "border-white/10"
      }`}
      value={r.email}
      title={r.email || undefined}
      onChange={(e) => update(r.id, { email: e.target.value })}
      placeholder="Email address (optional)"
    />
  );

  const renderEmailError = (r: EditableTransferRule) =>
    showEmailError(r) ? (
      <p className="mt-1 text-xs text-amber-300">Enter a valid email</p>
    ) : null;

  return (
    <section
      ref={sectionRef}
      className={`rounded-xl p-6 text-left overflow-hidden ${cardBorderClassName}`}
      style={{ background: "rgba(28, 28, 37, 0.8)" }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-[16rem]">
          <SectionHeading icon="ri-git-branch-line" className="!mb-0">
            Transfer routing
          </SectionHeading>
          <p className="mt-1 text-sm text-white/65">
            Triggers are matched against what the caller says (e.g. &quot;customer
            service&quot;). Use commas for multiple phrases in one row.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={reload}
            disabled={loading || saving}
            className="rounded-xl border border-white/15 px-3 py-2 text-sm text-white/85 hover:border-white/30 disabled:opacity-50"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || loading}
            className="w-auto whitespace-nowrap px-4 py-2 rounded-lg font-semibold transition-all"
            style={{
              background:
                saving || loading
                  ? "rgba(138, 43, 226, 0.4)"
                  : "linear-gradient(90deg, #8a2be2, #ff6a00)",
              color: "white",
              cursor: saving || loading ? "not-allowed" : "pointer",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            {saving ? "Saving…" : "Save rules"}
          </button>
        </div>
      </div>

      {status && (
        <div
          className={`mt-4 rounded-lg border px-3 py-2 text-sm ${
            status.startsWith("Saved")
              ? "border-emerald-500/40 bg-emerald-950/40 text-emerald-200"
              : "border-red-500/40 bg-red-950/30 text-red-200"
          }`}
        >
          {status}
        </div>
      )}

      {loading ? (
        <div className="mt-6 text-sm text-white/60">Loading rules…</div>
      ) : (
        <div className="mt-6 space-y-4">
          <div className="hidden md:block w-full min-w-0 overflow-x-auto">
            <table className="table-fixed w-full text-sm">
              <colgroup>
                {["14%", "20%", "16%", "11%", "8%", "21%", "10%"].map((w, i) => (
                  <col key={i} style={{ width: w }} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {["Description", "Keywords", "Phone", "Mode", "Message", "Email"].map((h) => (
                    <th
                      key={h}
                      className="text-left text-xs uppercase tracking-wide text-gray-500 font-medium pb-2 pr-3"
                    >
                      {h}
                    </th>
                  ))}
                  <th className="text-left text-xs uppercase tracking-wide text-gray-500 font-medium pb-2 pr-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={r.id}
                    className={`border ${
                      invalidRowId === r.id ? "border-amber-400/60" : "border-transparent"
                    }`}
                  >
                    <td className="py-2 pr-3 align-top">
                      <input
                        data-description-for={r.id}
                        aria-label="Description"
                        className={INPUT_CLASS}
                        value={r.description}
                        onChange={(e) => update(r.id, { description: e.target.value })}
                        placeholder="Customer Service"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <input
                        aria-label="Keywords"
                        className={INPUT_CLASS}
                        value={r.keyword}
                        onChange={(e) => update(r.id, { keyword: e.target.value })}
                        placeholder="customer service, billing"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <input
                        aria-label="Phone"
                        className={INPUT_CLASS}
                        value={r.number}
                        onChange={(e) => update(r.id, { number: e.target.value })}
                        placeholder="949-555-1212"
                      />
                      {renderQuickSelect(r, "table")}
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        aria-label="Mode"
                        className={INPUT_CLASS}
                        value={r.mode}
                        onChange={(e) =>
                          update(r.id, {
                            mode: e.target.value as "screened" | "direct",
                          })
                        }
                      >
                        <option value="screened">Screened</option>
                        <option value="direct">Direct</option>
                      </select>
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <div className="flex flex-col gap-1.5 text-xs text-white/70">
                        <label className="flex flex-wrap items-center gap-x-2">
                          <input
                            type="checkbox"
                            checked={r.message_to_text}
                            onChange={(e) =>
                              update(r.id, { message_to_text: e.target.checked })
                            }
                          />
                          Text
                        </label>
                        <label className="flex flex-wrap items-center gap-x-2">
                          <input
                            type="checkbox"
                            checked={r.message_to_email}
                            onChange={(e) =>
                              update(r.id, { message_to_email: e.target.checked })
                            }
                          />
                          Email
                        </label>
                      </div>
                    </td>
                    <td className="py-2 pr-3 align-top">
                      {renderEmailInput(r, "", "Message email address")}
                      {renderEmailError(r)}
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <RowActions
                        index={i}
                        count={rows.length}
                        onMove={(dir) => move(r.id, dir)}
                        onRemove={() => remove(r.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-4">
            {rows.map((r, i) => (
              <div
                key={r.id}
                className={`grid gap-3 rounded-xl border bg-black/20 p-4 ${
                  invalidRowId === r.id ? "border-amber-400/60" : "border-white/10"
                }`}
              >
                <label className="block text-xs text-gray-400">
                  Department / description
                  <input
                    data-description-for={r.id}
                    className={`mt-1 ${INPUT_CLASS}`}
                    value={r.description}
                    onChange={(e) => update(r.id, { description: e.target.value })}
                    placeholder="Customer Service"
                  />
                </label>
                <label className="block text-xs text-gray-400">
                  Trigger keyword(s)
                  <input
                    className={`mt-1 ${INPUT_CLASS}`}
                    value={r.keyword}
                    onChange={(e) => update(r.id, { keyword: e.target.value })}
                    placeholder="customer service, billing"
                  />
                </label>
                <div>
                  <label className="block text-xs text-gray-400">
                    Phone number
                    <input
                      className={`mt-1 ${INPUT_CLASS}`}
                      value={r.number}
                      onChange={(e) => update(r.id, { number: e.target.value })}
                      placeholder="949-555-1212"
                    />
                  </label>
                  {renderQuickSelect(r, "card")}
                </div>
                <label className="block text-xs text-gray-400">
                  Mode
                  <select
                    className={`mt-1 ${INPUT_CLASS}`}
                    value={r.mode}
                    onChange={(e) =>
                      update(r.id, {
                        mode: e.target.value as "screened" | "direct",
                      })
                    }
                  >
                    <option value="screened">Screened</option>
                    <option value="direct">Direct</option>
                  </select>
                </label>
                <div className="flex flex-col gap-2 text-xs text-white/70">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={r.message_to_text}
                      onChange={(e) =>
                        update(r.id, { message_to_text: e.target.checked })
                      }
                    />
                    SMS message if unavailable
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={r.message_to_email}
                      onChange={(e) =>
                        update(r.id, { message_to_email: e.target.checked })
                      }
                    />
                    Email message if unavailable
                  </label>
                </div>
                <div>
                  <label className="block text-xs text-gray-400">
                    <span className="font-medium text-white/75">
                      Message email address
                    </span>
                    <span className="mt-0.5 block font-normal text-white/45">
                      Email for message fallback — optional unless “Email message if
                      unavailable” is checked; if checked, a valid address is required
                      to save.
                    </span>
                    {renderEmailInput(r, "mt-1 max-w-xl md:max-w-2xl")}
                  </label>
                  {renderEmailError(r)}
                </div>
                <div className="flex justify-end">
                  <RowActions
                    index={i}
                    count={rows.length}
                    onMove={(dir) => move(r.id, dir)}
                    onRemove={() => remove(r.id)}
                  />
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500">
            Screened: caller holds while your team accepts. Direct: transfers immediately.
          </p>

          <button
            type="button"
            onClick={addRow}
            className="rounded-xl border border-dashed border-white/20 px-4 py-2 text-sm text-white/75 hover:border-white/35"
          >
            + Add rule
          </button>
        </div>
      )}
    </section>
  );
}
